import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import AddBranchModal from '../../../components/modals/AddBranchModal'
import EditBranchModal from '../../../components/modals/EditBranchModal'
import DeleteConfirmationModal from '../../../components/modals/DeleteConfirmationModal'
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  Clock,
  Phone,
  Mail,
  MapPin,
  Filter,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Building,
  X,
  Package
} from 'lucide-react'
import BranchService from '../../../services/branchService'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'

// Enhanced Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2 hover:bg-black hover:bg-opacity-20 rounded p-1">
      <X className="h-4 w-4" />
    </button>
  </div>
)

// Enhanced Stats Card Component
const StatsCard = ({ icon: Icon, title, value, color = "orange" }) => (
  <Card className="hover:shadow-md transition-shadow duration-200">
    <CardContent className="p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
)

export const Branches = () => {
  const [branches, setBranches] = useState([])
  const [filteredBranches, setFilteredBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [actionLoading, setActionLoading] = useState({})
  const [toast, setToast] = useState(null)
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    types: 0
  })

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to validate branch object
  const validateBranch = (branch) => {
    if (!branch || typeof branch !== 'object') return false
    return branch._id && branch.name
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Calculate stats
  const calculateStats = (branchList) => {
    const uniqueTypes = new Set(branchList.map(branch => branch.type).filter(Boolean))
    
    return {
      total: branchList.length,
      active: branchList.filter(branch => branch.isActive).length,
      inactive: branchList.filter(branch => !branch.isActive).length,
      types: uniqueTypes.size
    }
  }

  // Enhanced refresh function
  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered')
    setLoading(true)
    setError(null)
    
    try {
      const response = await BranchService.getAllBranches()
      
      if (response && response.success) {
        const branchesData = safeGet(response, 'data.branches', [])
        const validBranches = Array.isArray(branchesData) 
          ? branchesData.filter(validateBranch)
          : []
        setBranches(validBranches)
        setStats(calculateStats(validBranches))
      } else {
        setError(safeGet(response, 'message', 'Failed to fetch branches'))
      }
      
      showToast('Data refreshed successfully', 'success')
    } catch (error) {
      console.error('❌ Error during refresh:', error)
      setError(error?.message || 'Failed to fetch branches')
      showToast('Error refreshing data', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Fetch branches using service
  const fetchBranches = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await BranchService.getAllBranches()
      
      if (response && response.success) {
        const branchesData = safeGet(response, 'data.branches', [])
        const validBranches = Array.isArray(branchesData) 
          ? branchesData.filter(validateBranch)
          : []
        setBranches(validBranches)
        setStats(calculateStats(validBranches))
      } else {
        setError(safeGet(response, 'message', 'Failed to fetch branches'))
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
      setError(error?.message || 'Failed to fetch branches')
    } finally {
      setLoading(false)
    }
  }

  // Apply filters
  const applyFilters = () => {
    let filtered = [...branches]

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(branch => {
        const name = safeGet(branch, 'name', '').toLowerCase()
        const code = safeGet(branch, 'code', '').toLowerCase()
        const email = safeGet(branch, 'email', '').toLowerCase()
        const phone = safeGet(branch, 'phoneNumber', '')
        const type = safeGet(branch, 'type', '').toLowerCase()
        
        return name.includes(searchLower) ||
               code.includes(searchLower) ||
               email.includes(searchLower) ||
               phone.includes(searchLower) ||
               type.includes(searchLower)
      })
    }

    // Type filter
    if (typeFilter) {
      filtered = filtered.filter(branch => branch.type === typeFilter)
    }

    // Status filter
    if (statusFilter) {
      const isActive = statusFilter === 'active'
      filtered = filtered.filter(branch => branch.isActive === isActive)
    }

    setFilteredBranches(filtered)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setTypeFilter('')
    setStatusFilter('')
  }

  // Get unique branch types for filter dropdown
  const getBranchTypes = () => {
    const types = [...new Set(branches.map(branch => branch.type).filter(Boolean))]
    return types.sort()
  }

  // Handle branch added
  const handleBranchAdded = (newBranch) => {
    if (validateBranch(newBranch)) {
      setBranches(prev => [newBranch, ...prev])
      showToast('Branch created successfully!', 'success')
    }
  }

  // Handle branch updated
  const handleBranchUpdated = (updatedBranch) => {
    if (validateBranch(updatedBranch)) {
      setBranches(prev => prev.map(branch => 
        safeGet(branch, '_id') === safeGet(updatedBranch, '_id') 
          ? { ...branch, ...updatedBranch }
          : branch
      ))
      showToast('Branch updated successfully!', 'success')
    }
  }

  // Handle branch deleted
  const handleBranchDeleted = (branchId) => {
    if (branchId) {
      setBranches(prev => prev.filter(branch => safeGet(branch, '_id') !== branchId))
      showToast('Branch deleted successfully', 'success')
    }
  }

  // Handle status toggle
  const handleToggleStatus = async (branchId, currentStatus, branchName) => {
    if (!branchId) return
    
    try {
      setActionLoading(prev => ({ ...prev, [branchId]: 'toggling' }))
      
      const response = await BranchService.toggleBranchStatus(branchId, !currentStatus)
      
      if (response && response.success) {
        setBranches(prev => prev.map(branch => 
          safeGet(branch, '_id') === branchId 
            ? { ...branch, isActive: !currentStatus }
            : branch
        ))
        const safeBranchName = branchName || 'Branch'
        showToast(
          `${safeBranchName} ${!currentStatus ? 'activated' : 'deactivated'} successfully`, 
          'success'
        )
      } else {
        showToast(safeGet(response, 'message', 'Failed to update branch status'), 'error')
      }
    } catch (error) {
      console.error('Error updating branch status:', error)
      showToast(error?.message || 'Failed to update branch status', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [branchId]: null }))
    }
  }

  // Handle edit button click
  const handleEditClick = (branch) => {
    setSelectedBranch(branch)
    setShowEditModal(true)
  }

  // Handle delete button click
  const handleDeleteClick = (branch) => {
    setSelectedBranch(branch)
    setShowDeleteModal(true)
  }

  useEffect(() => {
    fetchBranches()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [branches, searchTerm, typeFilter, statusFilter])

  if (loading) {
    return (
      <InlineLoader text="Loading branches..." />
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Enhanced Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Branches</h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="flex items-center space-x-2 border-gray-300 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          
          
          
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Branch
          </Button>
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={Building}
          title="Total Branches"
          value={stats.total.toLocaleString()}
          color="blue"
        />
        <StatsCard
          icon={CheckCircle}
          title="Active Branches"
          value={stats.active.toLocaleString()}
          color="green"
        />
        <StatsCard
          icon={XCircle}
          title="Inactive Branches"
          value={stats.inactive.toLocaleString()}
          color="red"
        />
        <StatsCard
          icon={Package}
          title="Branch Types"
          value={stats.types.toLocaleString()}
          color="purple"
        />
      </div>

      {/* Enhanced Filters Card */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Branches</h2>
            </div>
            {(searchTerm || typeFilter || statusFilter) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
                size="sm"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search branches..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value || '')}
                className="pl-10 h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
            </div>

            {/* Type Filter */}
            <div className="relative">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Types</option>
                {getBranchTypes().map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
            Showing <span className="font-medium text-gray-900">{filteredBranches.length}</span> of <span className="font-medium text-gray-900">{branches.length}</span> branches
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Branch List */}
      <Card className="shadow-sm">
        <CardHeader className="border-b border-gray-200">
          <CardTitle className="flex items-center space-x-2">
            <Building className="h-5 w-5 text-orange-600" />
            <span>Branches Directory</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {error ? (
            <div className="text-center py-12 px-6">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Branches</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button 
                onClick={handleRefresh} 
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : filteredBranches.length === 0 ? (
            <div className="text-center py-12 px-6">
              <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {branches.length === 0 ? 'No branches found' : 'No branches match your filters'}
              </h3>
              <p className="text-gray-600 mb-4">
                {branches.length === 0 
                  ? 'Get started by adding your first branch.' 
                  : 'Try adjusting your search criteria.'}
              </p>
              {branches.length === 0 && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Branch
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full min-w-full bg-white">
                  {/* Table Header */}
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mobile
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Address
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBranches.map((branch) => {
                      if (!validateBranch(branch)) return null
                      
                      const branchId = safeGet(branch, '_id')
                      const isLoading = actionLoading[branchId]
                      
                      return (
                        <tr key={branchId} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <Building className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {safeGet(branch, 'name', 'Unknown Branch')}
                                </p>
                                {/* <p className="text-sm text-gray-500">
                                  {safeGet(branch, 'code', 'N/A')}
                                </p> */}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {safeGet(branch, 'type') ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <Package className="h-3 w-3 mr-1" />
                                {safeGet(branch, 'type')}
                              </span>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {safeGet(branch, 'phoneNumber', 'N/A')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Mail className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {safeGet(branch, 'email', 'N/A')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2 max-w-xs">
                              <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-900 truncate" title={safeGet(branch, 'address')}>
                                {safeGet(branch, 'address', 'N/A')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleToggleStatus(
                                branchId, 
                                safeGet(branch, 'isActive', false), 
                                safeGet(branch, 'name')
                              )}
                              disabled={isLoading}
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${
                                safeGet(branch, 'isActive', false) 
                                  ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200'
                              } ${
                                isLoading 
                                  ? 'opacity-50 cursor-not-allowed' 
                                  : 'cursor-pointer hover:opacity-80'
                              }`}
                            >
                              {isLoading === 'toggling' && (
                                <ButtonSpinner />
                              )}
                              {/* {safeGet(branch, 'isActive', false) ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <XCircle className="h-3 w-3 mr-1" />
                              )} */}
                              {safeGet(branch, 'isActive', false) ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-1">
                              <button 
                                onClick={() => setSelectedBranch(branch)}
                                disabled={isLoading}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => handleEditClick(branch)}
                                disabled={isLoading}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="Edit Branch"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => handleDeleteClick(branch)}
                                disabled={isLoading}
                                className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                title="Delete Branch"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {filteredBranches.map((branch) => {
                  if (!validateBranch(branch)) return null
                  
                  const branchId = safeGet(branch, '_id')
                  const isLoading = actionLoading[branchId]
                  
                  return (
                    <Card key={branchId} className="shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Branch Header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <Building className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {safeGet(branch, 'name', 'Unknown Branch')}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {safeGet(branch, 'code', 'N/A')}
                                </p>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              safeGet(branch, 'isActive', false) 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {safeGet(branch, 'isActive', false) ? 'Active' : 'Inactive'}
                            </span>
                          </div>

                          {/* Branch Details */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-gray-500">Type</p>
                              {safeGet(branch, 'type') ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <Package className="h-3 w-3 mr-1" />
                                  {safeGet(branch, 'type')}
                                </span>
                              ) : (
                                <p className="text-gray-400">N/A</p>
                              )}
                            </div>
                            <div>
                              <p className="text-gray-500">Mobile</p>
                              <p className="text-gray-900">{safeGet(branch, 'phoneNumber', 'N/A')}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-gray-500">Email</p>
                              <p className="text-gray-900">{safeGet(branch, 'email', 'N/A')}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-gray-500">Address</p>
                              <p className="text-gray-900 line-clamp-2">{safeGet(branch, 'address', 'N/A')}</p>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
                            <button 
                              onClick={() => setSelectedBranch(branch)}
                              disabled={isLoading}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleEditClick(branch)}
                              disabled={isLoading}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="Edit Branch"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteClick(branch)}
                              disabled={isLoading}
                              className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                              title="Delete Branch"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Branch Modal */}
      <AddBranchModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onBranchAdded={handleBranchAdded}
      />

      {/* Edit Branch Modal */}
      <EditBranchModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedBranch(null)
        }}
        onBranchUpdated={handleBranchUpdated}
        branch={selectedBranch}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedBranch(null)
        }}
        onBranchDeleted={handleBranchDeleted}
        branch={selectedBranch}
      />

      {/* Branch Details Modal */}
      {selectedBranch && validateBranch(selectedBranch) && !showEditModal && !showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Branch Details</h3>
              <button
                onClick={() => setSelectedBranch(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">Name</label>
                  <p className="text-gray-900 font-medium">
                    {safeGet(selectedBranch, 'name', 'Unknown')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">Type</label>
                  {safeGet(selectedBranch, 'type') ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Building className="h-3 w-3 mr-1" />
                      {safeGet(selectedBranch, 'type')}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">Not specified</span>
                  )}
                </div>
              </div>
              
              {safeGet(selectedBranch, 'phoneNumber') && (
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">Phone Number</label>
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">{safeGet(selectedBranch, 'phoneNumber')}</p>
                  </div>
                </div>
              )}
              
              {safeGet(selectedBranch, 'email') && (
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">Email</label>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">{safeGet(selectedBranch, 'email')}</p>
                  </div>
                </div>
              )}
              
              {safeGet(selectedBranch, 'address') && (
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">Address</label>
                  <div className="flex items-start space-x-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <p className="text-gray-900">{safeGet(selectedBranch, 'address')}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">Status</label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    safeGet(selectedBranch, 'isActive', false) 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {safeGet(selectedBranch, 'isActive', false) ? (
                      <>
                        {/* <CheckCircle className="h-3 w-3 mr-1" /> */}
                        Active
                      </>
                    ) : (
                      <>
                        {/* <XCircle className="h-3 w-3 mr-1" /> */}
                        Inactive
                      </>
                    )}
                  </span>
                </div>
                {/* <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">Code</label>
                  <p className="text-gray-900 font-mono text-sm">
                    {safeGet(selectedBranch, 'code', 'Not assigned')}
                  </p>
                </div> */}
              </div>
              
              {safeGet(selectedBranch, 'createdAt') && (
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">Created Date</label>
                  <p className="text-gray-900">
                    {new Date(safeGet(selectedBranch, 'createdAt')).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-6">
              <button
                onClick={() => setSelectedBranch(null)}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}